// @ts-check
import { useSelector, useDispatch } from 'react-redux';
import withGitIntegrationsModals from '../gitIntegrations/withGitIntegrationsModals';
import Icon from 'components/base/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { gitStreamWizardModalOptions } from 'modals/modal-options-factory';
import { repositoriesSelector } from 'state/selectors/repositories';
import { updateRepository, getSensorAuthorizations } from 'state/callers';
import {
  PROVIDER_NAMES,
  REPOSITORIES_STATE,
  TOKEN_VERSION_FOR_BB_GITSTREAM_INTEGRATION,
  TRACKING
} from 'common/constants/constants';
import {
  getDisableGitStreamConfirmationModalOptions,
  isCmRepo
} from './gitStream.service';
import * as TrackingService from 'common/services/tracking.service';
import { LBButton } from '@linearb/buzz';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';

const canConnectBitbucketDirectly = (
  isConnect,
  cmRepo,
  sensorName,
  tokenVersion
) => {
  return (
    isConnect &&
    cmRepo &&
    sensorName === PROVIDER_NAMES.bitbucket &&
    tokenVersion === TOKEN_VERSION_FOR_BB_GITSTREAM_INTEGRATION
  );
};

const canConnectGitlabDirectly = (isConnect, cmRepo, sensorName) => {
  return isConnect && cmRepo && sensorName === PROVIDER_NAMES.gitlab;
};

const canConnectWithoutModal = (
  isConnect,
  cmRepo,
  sensorName,
  tokenVersion
) => {
  return (
    canConnectBitbucketDirectly(isConnect, cmRepo, sensorName, tokenVersion) ||
    canConnectGitlabDirectly(isConnect, cmRepo, sensorName)
  );
};

const GitStreamIntegrationButton = ({
  gitIntegration,
  isDisabled = false,
  toggleModal,
  openRepositoriesModal,
  onDisconnect
}) => {
  const dispatch = useDispatch();
  const { getSettingOrDefinitionValue } = useSettingsService();
  const isGitHubAppIntegrationEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isGitHubAppIntegrationEnabled
  });
  const {
    id,
    sensor_name: sensorName,
    name: integrationName,
    gitstream_enabled: gitStreamEnabled
  } = gitIntegration;
  const isConnect = !gitStreamEnabled;
  const repositories = useSelector(repositoriesSelector);

  const clickType = isConnect ? 'connect_gitstream' : 'disconnect_gitstream';
  const tokenVersion = gitIntegration?.profile?.token_version;

  const onClickButton = async e => {
    e.stopPropagation();

    const cmRepo = repositories.find(
      ({ name, sensor_authorization_id }) =>
        isCmRepo(name) && sensor_authorization_id === id
    );

    if (canConnectWithoutModal(isConnect, cmRepo, sensorName, tokenVersion)) {
      // we enable gitstream for cm repo and don't show modal
      await updateRepository(cmRepo.id, {
        state: REPOSITORIES_STATE.ACTIVE, // enable cm repo for linearB
        extra: { is_gitstream_enabled: true }
      })(dispatch);
      await getSensorAuthorizations({
        withGitStreamEnabled: true,
        withoutGitHubApp: !isGitHubAppIntegrationEnabled
      })(dispatch);

      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.GIT_INTEGRATIONS,
        integration_name: integrationName,
        integration_type: sensorName,
        click_type: clickType
      });
    } else {
      // open in a cm repo selector - using this flag isConnectGitStreamFlow for gitstream reauth
      toggleModal(
        isConnect
          ? gitStreamWizardModalOptions({
              sensorAuth: gitIntegration,
              integrationName,
              sensorName,
              onDoneClick: () => {
                if (cmRepo) {
                  toggleModal();
                } else {
                  openRepositoriesModal({
                    sensorAuth: gitIntegration,
                    isConnectGitStreamFlow: true
                  });
                }
              }
            })
          : getDisableGitStreamConfirmationModalOptions({
              onDisconnect,
              onCancel: toggleModal
            })
      );
    }
  };
  return (
    <Tooltip
      disableHoverListener={!isDisabled}
      placement='top'
      title={
        <Flex padding='1.45rem' direction='column' width='28rem' borderBox>
          <Flex mb='1.45rem'>
            <Icon name='erroricon' color='orangePeel' size='normal' />
            <Text weight='500' size='x-medium' pl='0.7rem'>
              This integration cannot be connected to gitStream
            </Text>
          </Flex>
          <Text size='x-medium' color='shuttleGray'>
            There is already one integration connected to gitSteam. You need to
            disconnect it if you prefer to connect this integration.
          </Text>
        </Flex>
      }
    >
      <LBButton
        size='sm'
        disabled={isDisabled}
        variant='outline'
        onClick={onClickButton}
        data-testid='gitstream-connect-btn'
      >
        <Icon
          name={isConnect ? 'LinkIcon' : 'UnlinkIcon'}
          size='smaller'
          color={isDisabled ? 'silverSand' : undefined}
        />
        {`${isConnect ? 'Connect' : 'Disconnect'} gitStream`}
      </LBButton>
    </Tooltip>
  );
};

export default withGitIntegrationsModals(GitStreamIntegrationButton);
